import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSellerOrders } from "../hooks/useSellerOrders";
import {
  Package,
  ShieldCheck,
  FileCheck,
  ArrowRight,
  DollarSign,
  Star,
  ShoppingCart,
  Zap,
  TrendingUp,
} from "lucide-react";
import AccountStatusCard from "./AccountStatus";
import StatsChart from "../components/Seller/StatsChart";
import { OrderStatus } from "../types/order.type";

const STATUS_COLORS: Record<OrderStatus, string> = {
  DELIVERED: "badge-success",
  SHIPPED: "badge-info",
  PROCESSING: "badge-warning",
  PENDING: "badge-muted",
  CANCELLED: "badge-error",
};

const QUICK_ACTIONS = [
  {
    label: "Add New Product",
    to: "/add-product",
    icon: Package,
    color: "var(--primary)",
    bg: "rgba(13, 40, 24, 0.05)",
  },
  {
    label: "View All Orders",
    to: "/orders",
    icon: ShoppingCart,
    color: "var(--secondary)",
    bg: "rgba(45, 106, 79, 0.05)",
  },
  {
    label: "Submit KYC",
    to: "/kyc",
    icon: FileCheck,
    color: "var(--success)",
    bg: "rgba(45, 106, 79, 0.1)",
  },
  {
    label: "Verify GST",
    to: "/gst",
    icon: ShieldCheck,
    color: "var(--accent)",
    bg: "rgba(212, 163, 115, 0.1)",
  },
];

const DashboardPage = () => {
  const { seller } = useAuth();
  const {
    orders = [],
    todayCount,
    dateWiseCount = [],
    loading = false,
    fetchOrders,
    fetchDashboardStats,
  } = useSellerOrders();

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    fetchOrders({ limit: 10 });
    fetchDashboardStats();
  }, [fetchOrders, fetchDashboardStats]);

  // Safe calculations with proper null/undefined checks
  const totalRevenue =
    orders?.reduce((acc, order) => acc + (order?.totalAmount || 0), 0) || 0;

  const KPI_DATA = [
    {
      label: "Today's Orders",
      val: todayCount?.toString() || "0",
      change: "Live",
      dir: "up",
      icon: ShoppingCart,
    },
    {
      label: "Estimated Revenue",
      val: `₹${totalRevenue?.toLocaleString() || "0"}`,
      change: "+10%",
      dir: "up",
      icon: DollarSign,
    },
    {
      label: "Total Orders",
      val: orders?.length?.toString() || "0",
      change: "Recent",
      dir: "up",
      icon: Package,
    },
    {
      label: "Customer Rating",
      val: "4.8 ★",
      change: "Stable",
      dir: "up",
      icon: Star,
    },
  ];

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Get seller email safely
  const sellerName = seller?.email?.split("@")[0] || "Seller";

  return (
    <div className="pt-24 pb-16 min-h-screen page-root">
      <div className="max-w-7xl mx-auto px-6">
        {/* ── Welcome banner ── */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/10 rounded-2xl p-8 mb-8 flex flex-wrap justify-between items-center gap-6 animate-slide-up">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl font-extrabold text-slate-900">
              {greeting}, {sellerName}! 👋
            </h2>
            <p className="text-sm text-slate-500">
              Here's what's happening with your store today.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/add-product" className="btn btn-primary">
              <Package size={16} /> Add Product
            </Link>
            <Link to="/orders" className="btn btn-ghost">
              <ShoppingCart size={16} /> View Orders
            </Link>
          </div>
        </div>

        {/* ── KPI cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-slide-up">
          {KPI_DATA.map(({ label, val, change, dir, icon: Icon }) => (
            <div
              key={label}
              className="glass-card p-6 relative overflow-hidden group hover:border-primary transition-all duration-300"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary transform -translate-y-full group-hover:translate-y-0 transition-transform" />
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
                {label}
              </div>
              <div className="font-display text-2xl font-extrabold text-slate-900 mb-1">
                {val}
              </div>
              <div
                className={`text-[11px] font-medium flex items-center gap-1 ${
                  dir === "up" ? "text-emerald-600" : "text-red-600"
                }`}
              >
                {change}
              </div>
              <div className="absolute top-1/2 right-4 -translate-y-1/2 opacity-[0.03] group-hover:opacity-[0.08] group-hover:scale-110 transition-all">
                <Icon size={64} />
              </div>
            </div>
          ))}
        </div>

        {/* ── Main grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
          <div className="lg:col-span-2 space-y-6">
            {/* Sales Chart (New) */}
            <div className="h-[400px]">
              <StatsChart data={dateWiseCount || []} loading={loading} />
            </div>

            {/* Recent orders */}
            <div className="inner-card overflow-hidden">
              <div className="inner-card-header">
                <ShoppingCart size={18} className="text-secondary" />
                <h2 className="inner-card-title">Recent Orders</h2>
                <Link
                  to="/orders"
                  className="text-xs font-bold text-primary hover:underline ml-auto flex items-center gap-1"
                >
                  View All <ArrowRight size={12} />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-50">
                      <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                        ID
                      </th>
                      <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Main Item
                      </th>
                      <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Amount
                      </th>
                      <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Status
                      </th>
                      <th className="py-3 px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {loading ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-8 text-center text-slate-400"
                        >
                          Loading orders...
                        </td>
                      </tr>
                    ) : orders && orders.length > 0 ? (
                      orders.slice(0, 5).map((o) => (
                        <tr
                          key={o?.id || Math.random()}
                          className="group hover:bg-slate-50/50 transition-colors"
                        >
                          <td className="py-4 px-4 text-xs font-bold text-primary text-center">
                            <Link to={`/orders/${o?.id || ""}`}>
                              #{o?.id?.slice(0, 6) || "N/A"}
                            </Link>
                          </td>
                          <td className="py-4 px-4 text-sm text-slate-600 font-medium">
                            {o?.items?.[0]?.variant?.product?.name ||
                              o?.items?.[0]?.productName ||
                              "Multiple Items"}
                            {o?.items && o.items.length > 1 && (
                              <span className="text-[10px] ml-1 text-slate-400">
                                +{o.items.length - 1} more
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-sm font-bold text-slate-900">
                            ₹{(o?.totalAmount || 0).toLocaleString()}
                          </td>
                          <td className="py-4 px-4">
                            <span
                              className={`badge ${o?.status && STATUS_COLORS[o.status] ? STATUS_COLORS[o.status] : "badge-muted"}`}
                            >
                              {o?.status || "UNKNOWN"}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-xs text-slate-400">
                            {formatDate(o?.createdAt || "")}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-8 text-center text-slate-400"
                        >
                          No recent orders
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-6">
            {/* Daily Activity */}
            <div className="inner-card">
              <div className="inner-card-header">
                <TrendingUp size={18} className="text-emerald-500" />
                <h2 className="inner-card-title">Day History</h2>
              </div>
              <div className="space-y-4">
                {dateWiseCount && dateWiseCount.length > 0 ? (
                  dateWiseCount.slice(0, 5).map((day, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100"
                    >
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-900">
                          {formatDate(day?.createdAt || "")}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          Orders Received
                        </span>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center font-bold text-primary">
                        {day?._count?.id || 0}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-400 text-center py-4">
                    No recent activity data available
                  </p>
                )}
              </div>
            </div>

            {/* Quick actions */}
            <div className="inner-card">
              <div className="inner-card-header">
                <Zap size={18} className="text-amber-500" />
                <h2 className="inner-card-title">Quick Actions</h2>
              </div>
              <div className="flex flex-col gap-2.5">
                {QUICK_ACTIONS.map(({ label, to, icon: Icon, color, bg }) => (
                  <Link
                    key={label}
                    to={to}
                    className="flex items-center gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-medium text-slate-600 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all group"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: bg }}
                    >
                      <Icon size={17} style={{ color }} />
                    </div>
                    {label}
                    <ArrowRight
                      size={14}
                      className="ml-auto opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                ))}
              </div>
            </div>

            <AccountStatusCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
